import React, { useCallback, useEffect, useMemo, useReducer, useRef } from "react"
import { TabBody, TabHeader, Tabs } from "@ciq/shared/components/tabs"
import Api from "@ciq/shared/api/api"
import { ErrorToast } from "@ciq/shared/utils/toast"
import { useAppContext } from "@ciq/shared/hooks/context"
import Indicator from "@ciq/shared/components/loader"
import { Sidebar } from "./components/Sidebar"
import { SidebarFilterPills } from "./components/SidebarFilterPills"
import { FloorPlansTab } from "./components/FloorPlansTab"
import { useFileUploadSidebar } from "./hooks/useFileUploadSidebar"
import { useFileUpload } from "./hooks/useFileUpload"
import { FileDropZone } from "./components/FileDropZone"

const FLOOR_PLANS_TAB = { id: "floor-plans", label: "Floor Plans" }
const TABS = [FLOOR_PLANS_TAB]

/**
 * @typedef {Object} FileUploadState
 * @property {string} selectedTabId - Currently active tab ID
 * @property {boolean} loading - Whether file data is being fetched
 * @property {Array} files - Array of uploaded file objects
 */

/** @type {FileUploadState} */
const DEFAULT_DATA = {
  selectedTabId: FLOOR_PLANS_TAB.id,
  loading: false,
  files: [],
}

const SET_LOADING = "SET_LOADING"
const SET_ACTIVE_TAB = "SET_ACTIVE_TAB"
const SET_FILES = "SET_FILES"

/**
 * Reducer for the File Upload page state.
 *
 * @param {FileUploadState} state - Current state
 * @param {Object} action - Action with type and payload
 * @returns {FileUploadState} New state
 */
export const FileUploadReducer = (state = {}, action) => {
  switch (action.type) {
    case SET_LOADING:
      return { ...state, loading: action.payload }
    case SET_ACTIVE_TAB:
      return { ...state, selectedTabId: action.payload }
    case SET_FILES:
      return { ...state, files: action.payload }
    default:
      throw new Error(`Unknown action type: ${action.type}`)
  }
}

/**
 * File Upload page with sidebar layout and tab system.
 * Loads uploaded files on mount and displays them in a sidebar + content layout.
 *
 * @returns {React.ReactElement} The file upload page
 */
export default function FileUploadView() {
  const { schoolId } = useAppContext()
  const [state, dispatch] = useReducer(FileUploadReducer, DEFAULT_DATA)
  const {
    sidebarItems,
    selectedItemId,
    onSelectItem,
    activeFilter,
    onFilterChange,
    filterCounts,
    contentHeader,
    isLoading: sidebarLoading,
    expandedBuildings,
    onToggleBuilding,
  } = useFileUploadSidebar({ files: state.files, schoolId })

  const setSelectedTab = tab => dispatch({ type: SET_ACTIVE_TAB, payload: tab.id })

  const fetchControllerRef = useRef(null)

  const fetchFiles = useCallback(() => {
    fetchControllerRef.current?.abort()
    const controller = new AbortController()
    fetchControllerRef.current = controller

    Api.get(`schools/${schoolId}/uploaded-files`, { signal: controller.signal })
      .then(response => {
        if (controller.signal.aborted) return
        dispatch({ type: SET_FILES, payload: response.data.files || [] })
        dispatch({ type: SET_LOADING, payload: false })
      })
      .catch(e => {
        if (controller.signal.aborted) return
        dispatch({ type: SET_LOADING, payload: false })
        if (e.handled) return
        console.error(e.error)
        ErrorToast("Unable to fetch uploaded files")
      })
  }, [schoolId])

  useEffect(() => {
    if (schoolId == null) return

    dispatch({ type: SET_LOADING, payload: true })
    dispatch({ type: SET_FILES, payload: [] })
    fetchFiles()

    return () => fetchControllerRef.current?.abort()
  }, [schoolId, fetchFiles])

  const {
    uploads,
    uploadFiles,
    retryFile,
    dismissFile,
  } = useFileUpload({
    schoolId,
    selectedItemId,
    sidebarItems,
    onUploadComplete: fetchFiles,
  })

  const isInbox = selectedItemId === "inbox"

  const filteredFiles = useMemo(() => {
    if (!state.files) return []
    if (isInbox) return state.files.filter(f => f.building_zone_id == null)
    return state.files.filter(f => f.building_zone_id === selectedItemId)
  }, [state.files, selectedItemId, isInbox])

  return (
    <div className="file-upload-layout">
      <h1 className="sr-only">File Upload</h1>
      <div>
        {!sidebarLoading && sidebarItems.length > 1 && (
          <>
            <h2 className="file-upload-sidebar-section-header">BUILDINGS</h2>
            <SidebarFilterPills
              activeFilter={activeFilter}
              onFilterChange={onFilterChange}
              counts={filterCounts}
            />
          </>
        )}
        <Sidebar
          selectedItemId={selectedItemId}
          onSelectItem={onSelectItem}
          items={sidebarItems}
          expandedBuildings={expandedBuildings}
          onToggleBuilding={onToggleBuilding}
        />
        <Indicator show={sidebarLoading} />
      </div>
      <div className="file-upload-content da-card">
        <div className="file-upload-content__header">
          <h2 className="file-upload-content-header">{contentHeader}</h2>
          {(filteredFiles.length > 0 || uploads.length > 0) && (
            <FileDropZone
              collapsed
              onFilesSelect={uploadFiles}
              onError={(msg) => ErrorToast(msg)}
            />
          )}
        </div>
        <Tabs>
          <TabHeader selectedTab={state.selectedTabId} items={TABS} onSelectTab={setSelectedTab} />
          <TabBody className="file-upload-tab-body">
            <FloorPlansTab
              show={state.selectedTabId === FLOOR_PLANS_TAB.id}
              files={filteredFiles}
              schoolId={schoolId}
              onFilesChanged={fetchFiles}
              loading={state.loading}
              onFilesSelect={uploadFiles}
              onError={(msg) => ErrorToast(msg)}
              uploads={uploads}
              onRetry={retryFile}
              onDismiss={dismissFile}
              isInbox={isInbox}
            />
          </TabBody>
        </Tabs>
        <Indicator show={state.loading} />
      </div>
    </div>
  )
}

export {
  DEFAULT_DATA,
  SET_LOADING,
  SET_ACTIVE_TAB,
  SET_FILES,
}
